import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Col,
    Form,
    Input,
    InputNumber,
    Row,
    Select,
    Space,
    Spin,
    Typography,
    message
} from 'antd';
import { DeleteOutlined, FileAddOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
    FiscalConfigurationResponse,
    FiscalProfileResponse,
    ClientDataLookupResponse,
    EffectiveTaxRate,
    OrganizationTaxConfigurationResponse,
    OrganizationPaymentMethod,
    ProfileResponse,
    RuntimeEnvironmentResponse
} from '../store/user/types';
import {
    getSalesDocumentFiscalConfigurationAPI,
    getSalesDocumentFiscalProfileAPI,
    getSalesDocumentOrganizationPaymentMethodsAPI,
    getSalesDocumentRuntimeEnvironmentAPI,
    getSalesDocumentClientByIdentificationAPI,
    getOrganizationTaxConfigurationAPI,
    postSalesDocumentDirectAPI,
    postSalesDocumentDirectPaymentAPI
} from '../store/user/restCallUser';

const { Text, Title } = Typography;

interface DirectSalesDocumentManagementProps {
    getProfile: any;
}

interface DirectInvoiceForm {
    organizationBillingDocumentTypeId: string;
    organizationFiscalPointOfEmissionId: string;
    organizationFiscalSequenceId: string;
    identificationType: string;
    identificationNumber: string;
    clientName: string;
    clientEmail?: string;
    clientPhone?: string;
    clientAddress?: string;
    details: DirectInvoiceDetail[];
    status: 'PENDIENTE' | 'PAGADO';
    registerPayment: boolean;
    organizationPaymentMethodId?: string;
    externalTransactionId?: string;
}

interface DirectInvoiceDetail {
    description: string;
    quantity: number;
    unitPrice: number;
    discountPercentage: number;
    taxRateDefinitionId?: string;
    taxTreatment: string;
    taxRate: number;
}

const getResponseData = (response: any) => response?.Response?.data || response?.Response;

const getOrganizationFromProfile = (profile?: ProfileResponse) => {
    const contract = profile?.contract || profile?.Contract || {};
    return {
        id: String(contract.organizationId || contract.OrganizationId || profile?.organizationId || ''),
        name: String(contract.organizationName || contract.OrganizationName || profile?.organizationName || ''),
        userId: profile?.userId || '',
        userName: profile?.fullName || profile?.email || ''
    };
};

function DirectSalesDocumentManagement({ getProfile }: DirectSalesDocumentManagementProps) {
    const [form] = Form.useForm<DirectInvoiceForm>();
    const [configuration, setConfiguration] = useState<FiscalConfigurationResponse | null>(null);
    const [profile, setProfile] = useState<FiscalProfileResponse | null>(null);
    const [paymentMethods, setPaymentMethods] = useState<OrganizationPaymentMethod[]>([]);
    const [effectiveTaxRates, setEffectiveTaxRates] = useState<EffectiveTaxRate[]>([]);
    const [runtimeEnvironment, setRuntimeEnvironment] = useState<RuntimeEnvironmentResponse | null>(null);
    const [organization, setOrganization] = useState<{ id: string; name: string; userId: string; userName: string }>({
        id: '', name: '', userId: '', userName: ''
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [clientLookupLoading, setClientLookupLoading] = useState(false);

    const selectedPointId = Form.useWatch('organizationFiscalPointOfEmissionId', form);
    const selectedDocumentTypeId = Form.useWatch('organizationBillingDocumentTypeId', form);
    const watchedDetails = Form.useWatch('details', form) || [];
    const registerPayment = Boolean(Form.useWatch('registerPayment', form));
    const selectedPaymentMethodId = Form.useWatch('organizationPaymentMethodId', form);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const identityProfile = await getProfile();
            const currentOrganization = getOrganizationFromProfile(identityProfile);
            if (!currentOrganization.id) {
                throw new Error('No se pudo identificar la organización emisora.');
            }
            setOrganization(currentOrganization);

            const [fiscalProfileResponse, configurationResponse, paymentResponse, runtimeResponse, taxResponse] = await Promise.all([
                getSalesDocumentFiscalProfileAPI(currentOrganization.id),
                getSalesDocumentFiscalConfigurationAPI(currentOrganization.id),
                getSalesDocumentOrganizationPaymentMethodsAPI(currentOrganization.id),
                getSalesDocumentRuntimeEnvironmentAPI(),
                getOrganizationTaxConfigurationAPI(currentOrganization.id)
            ]);

            if (runtimeResponse.StatusCode >= 200 && runtimeResponse.StatusCode < 300) {
                setRuntimeEnvironment(getResponseData(runtimeResponse) as RuntimeEnvironmentResponse);
            } else {
                setRuntimeEnvironment(null);
            }

            if (configurationResponse.StatusCode < 200 || configurationResponse.StatusCode >= 300) {
                throw new Error('No se pudo cargar la configuración fiscal.');
            }

            const fiscalConfiguration = getResponseData(configurationResponse) as FiscalConfigurationResponse;
            setConfiguration(fiscalConfiguration);
            setProfile(getResponseData(fiscalProfileResponse) as FiscalProfileResponse || null);

            if (paymentResponse.StatusCode >= 200 && paymentResponse.StatusCode < 300) {
                const methods = getResponseData(paymentResponse);
                setPaymentMethods(Array.isArray(methods) ? methods : methods?.items || methods?.paymentMethods || []);
            } else {
                setPaymentMethods([]);
            }

            const taxConfiguration = taxResponse.StatusCode >= 200 && taxResponse.StatusCode < 300
                ? getResponseData(taxResponse) as OrganizationTaxConfigurationResponse
                : null;
            const availableTaxes = taxConfiguration?.effectiveRates || [];
            setEffectiveTaxRates(availableTaxes);
            const defaultTax = availableTaxes.find(item => item.isOrganizationDefault)
                || availableTaxes.find(item => item.taxTreatment === 'NO_OBJETO')
                || availableTaxes[0];

            const firstType = (fiscalConfiguration.billingDocumentTypes || []).find(item => item.status === 'ACTIVO');
            const firstPoint = (fiscalConfiguration.pointsOfEmission || []).find(item => item.status === 'ACTIVO');
            const firstSequence = (fiscalConfiguration.sequences || []).find(item =>
                item.status === 'ACTIVO'
                && item.organizationBillingDocumentTypeId === firstType?.id
                && item.fiscalPointOfEmissionId === firstPoint?.id
            );
            const defaultPayment = (Array.isArray(getResponseData(paymentResponse))
                ? getResponseData(paymentResponse)
                : []).find((item: OrganizationPaymentMethod) => item.status === 'ACTIVO' && item.isDefault);

            form.setFieldsValue({
                organizationBillingDocumentTypeId: firstType?.id,
                organizationFiscalPointOfEmissionId: firstPoint?.id,
                organizationFiscalSequenceId: firstSequence?.id,
                identificationType: 'RUC',
                status: 'PAGADO',
                details: [{
                    description: '',
                    quantity: 1,
                    unitPrice: 0,
                    discountPercentage: 0,
                    taxRateDefinitionId: defaultTax?.id,
                    taxTreatment: defaultTax?.taxTreatment || 'NO_OBJETO',
                    taxRate: Number(defaultTax?.rate || 0)
                }],
                registerPayment: true,
                organizationPaymentMethodId: defaultPayment?.id
            });
        } catch (error: any) {
            message.error(error?.message || 'No se pudo cargar la configuración de facturación.');
        } finally {
            setLoading(false);
        }
    }, [form, getProfile]);

    useEffect(() => { void load(); }, [load]);

    const findExistingClient = async () => {
        const identificationNumber = String(form.getFieldValue('identificationNumber') || '').trim();
        if (!organization.id || identificationNumber.length < 5) return;

        setClientLookupLoading(true);
        try {
            const response = await getSalesDocumentClientByIdentificationAPI(organization.id, identificationNumber);
            if (response.StatusCode === 404) return;
            if (response.StatusCode < 200 || response.StatusCode >= 300) {
                throw new Error(response.Error?.message || 'No se pudo consultar el cliente.');
            }
            const raw = getResponseData(response);
            const client = (typeof raw === 'string' ? JSON.parse(raw) : raw) as ClientDataLookupResponse;
            form.setFieldsValue({
                identificationType: client.identificationType,
                identificationNumber: client.identificationNumber,
                clientName: client.name,
                clientEmail: client.email || undefined,
                clientPhone: client.phone || undefined,
                clientAddress: client.address || undefined
            });
            message.success('Datos del cliente recuperados de su última factura.');
        } catch (error: any) {
            message.error(error?.message || 'No se pudo consultar el cliente.');
        } finally {
            setClientLookupLoading(false);
        }
    };

    const activeTypes = useMemo(() => (configuration?.billingDocumentTypes || [])
        .filter(item => item.status === 'ACTIVO'), [configuration]);
    const activePoints = useMemo(() => (configuration?.pointsOfEmission || [])
        .filter(item => item.status === 'ACTIVO'), [configuration]);
    const activeSequences = useMemo(() => (configuration?.sequences || []).filter(item =>
        item.status === 'ACTIVO'
        && item.fiscalPointOfEmissionId === selectedPointId
        && item.organizationBillingDocumentTypeId === selectedDocumentTypeId
    ), [configuration, selectedPointId, selectedDocumentTypeId]);
    const activePaymentMethods = useMemo(() => paymentMethods.filter(item => item.status === 'ACTIVO'), [paymentMethods]);
    const selectedPaymentMethod = activePaymentMethods.find(item => item.id === selectedPaymentMethodId);
    const paymentRequiresReference = Boolean(selectedPaymentMethod?.requiresReference);
    const invoiceTotals = useMemo(() => watchedDetails.reduce((totals, item) => {
        const quantity = Number(item?.quantity || 0);
        const unitPrice = Number(item?.unitPrice || 0);
        const grossAmount = Number((quantity * unitPrice).toFixed(2));
        const discountAmount = Number((grossAmount * Number(item?.discountPercentage || 0) / 100).toFixed(2));
        const netAmount = Number((grossAmount - discountAmount).toFixed(2));
        const effectiveTaxRate = item?.taxTreatment === 'GRAVADO' ? Number(item?.taxRate || 0) : 0;
        const taxAmount = Number((netAmount * effectiveTaxRate / 100).toFixed(2));
        totals.subtotal += netAmount;
        totals.discount += discountAmount;
        totals.tax += taxAmount;
        totals.total += netAmount + taxAmount;
        return totals;
    }, { subtotal: 0, discount: 0, tax: 0, total: 0 }), [watchedDetails]);

    const handleSubmit = async (values: DirectInvoiceForm) => {
        if (!organization.id || !profile) {
            message.error('Primero complete y guarde la configuración fiscal del emisor.');
            return;
        }
        if (!values.organizationFiscalSequenceId) {
            message.error('Seleccione una secuencia fiscal activa para el punto y comprobante.');
            return;
        }
        if (values.registerPayment && !values.organizationPaymentMethodId) {
            message.error('Seleccione el método de pago de la factura.');
            return;
        }

        if (!values.details?.length) {
            message.error('Agregue al menos un ítem a la factura.');
            return;
        }
        const details = values.details.map(item => {
            const quantity = Number(item.quantity || 0);
            const unitPrice = Number(item.unitPrice || 0);
            const grossAmount = Number((quantity * unitPrice).toFixed(2));
            const discountPercentage = Number(item.discountPercentage || 0);
            const discountAmount = Number((grossAmount * discountPercentage / 100).toFixed(2));
            const netAmount = Number((grossAmount - discountAmount).toFixed(2));
            const taxRate = item.taxTreatment === 'GRAVADO' ? Number(item.taxRate || 0) : 0;
            const taxAmount = Number((netAmount * taxRate / 100).toFixed(2));
            return { ...item, quantity, unitPrice, grossAmount, discountPercentage, discountAmount, taxRate, taxAmount, receivableAmount: Number((netAmount + taxAmount).toFixed(2)) };
        });
        const receivableAmount = Number(details.reduce((total, item) => total + item.receivableAmount, 0).toFixed(2));
        setSaving(true);
        setResult(null);
        try {
            const documentResponse = await postSalesDocumentDirectAPI({
                organizationBillingDocumentTypeId: values.organizationBillingDocumentTypeId,
                organizationFiscalPointOfEmissionId: values.organizationFiscalPointOfEmissionId,
                organizationFiscalSequenceId: values.organizationFiscalSequenceId,
                client: {
                    identificationType: values.identificationType,
                    identificationNumber: values.identificationNumber,
                    name: values.clientName,
                    email: values.clientEmail || null,
                    phone: values.clientPhone || null,
                    address: values.clientAddress || null
                },
                parentId: null,
                issueDate: new Date().toISOString().slice(0, 10),
                status: values.registerPayment ? 'PAGADO' : values.status,
                issuerMsIdentityUserId: organization.userId || null,
                issuerMsIdentityUserName: organization.userName || null,
                issuerMsIdentityCenterId: null,
                issuerMsIdentityCenterName: null,
                msMedicalRecordOrderAttentionCode: null,
                details: details.map(item => ({
                    unitPrice: item.unitPrice,
                    quantity: item.quantity,
                    grossAmount: item.grossAmount,
                    discountPercentage: item.discountPercentage,
                    discountAmount: item.discountAmount,
                    taxRateDefinitionId: item.taxRateDefinitionId,
                    taxTreatment: item.taxTreatment,
                    taxRate: item.taxRate,
                    taxAmount: item.taxAmount,
                    receivableAmount: item.receivableAmount,
                    msIdentityProductId: null,
                    msIdentityProductName: null,
                    msIdentityServiceId: null,
                    msIdentityServiceName: item.description,
                    msIdentityServiceCategory: 'SERVICIO',
                    msIdentityServiceCode: null,
                    msKardexProductId: null,
                    msMedicalRecordMedicalOrderProductId: null
                }))
            });

            if (documentResponse.StatusCode < 200 || documentResponse.StatusCode >= 300) {
                throw new Error(documentResponse.Error?.message || 'No se pudo crear la factura directa.');
            }

            const document = getResponseData(documentResponse);
            let payment: any = null;
            if (values.registerPayment) {
                const paymentResponse = await postSalesDocumentDirectPaymentAPI({
                    salesDocumentId: document.id,
                    organizationPaymentMethodId: values.organizationPaymentMethodId,
                    amountPaid: receivableAmount,
                    amountReceived: receivableAmount,
                    externalTransactionId: values.externalTransactionId || null,
                    msIdentityUserId: organization.userId || null,
                    msIdentityUserName: organization.userName || null,
                    observation: 'Pago registrado junto con la factura directa'
                });
                if (paymentResponse.StatusCode < 200 || paymentResponse.StatusCode >= 300) {
                    throw new Error(paymentResponse.Error?.message || 'La factura se creó, pero el pago no pudo registrarse.');
                }
                payment = getResponseData(paymentResponse);
            }

            setResult({ document, payment });
            message.success(values.registerPayment ? 'Factura y pago directo registrados.' : 'Factura directa registrada.');
        } catch (error: any) {
            message.error(error?.message || 'No se pudo registrar la factura directa.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card
            title="Generar factura directa"
            extra={<Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>Actualizar</Button>}
        >
            <Alert
                type="info"
                showIcon
                message={`Emisor: ${profile?.legalName || organization.name || 'No configurado'}`}
                description={runtimeEnvironment
                    ? `Ambiente activo: ${runtimeEnvironment.label}. Esta operación no abre turno ni caja ni envía todavía el comprobante al SRI.`
                    : 'Esta operación no abre turno ni caja ni envía todavía el comprobante al SRI.'}
                style={{ marginBottom: 20 }}
            />
            {loading ? <Spin /> : (
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    {!effectiveTaxRates.length && <Alert type="warning" showIcon message="No existen tarifas de IVA vigentes" description="Configure una vigencia global y, si corresponde, una predeterminada para la organización antes de emitir." style={{ marginBottom: 16 }} />}
                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item name="organizationBillingDocumentTypeId" label="Comprobante" rules={[{ required: true }]}>
                                <Select options={activeTypes.map(item => ({ value: item.id, label: `${item.name} (${item.code})` }))} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="organizationFiscalPointOfEmissionId" label="Punto de emisión" rules={[{ required: true }]}>
                                <Select
                                    options={activePoints.map(item => ({ value: item.id, label: `${item.code} - ${item.description || 'Punto de emisión'}` }))}
                                    onChange={() => form.setFieldValue('organizationFiscalSequenceId', undefined)}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="organizationFiscalSequenceId" label="Secuencia fiscal" rules={[{ required: true }]}>
                                <Select options={activeSequences.map(item => ({ value: item.id, label: `${item.billingDocumentTypeName} - siguiente ${String(Number(item.lastNumber || 0) + 1).padStart(9, '0')}` }))} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Title level={5}>Cliente</Title>
                    <Row gutter={16}>
                        <Col xs={24} md={6}><Form.Item name="identificationType" label="Tipo identificación" rules={[{ required: true }]}><Select options={[{ value: 'RUC' }, { value: 'CEDULA', label: 'Cédula' }, { value: 'PASAPORTE' }]} /></Form.Item></Col>
                        <Col xs={24} md={6}><Form.Item name="identificationNumber" label="Identificación" rules={[{ required: true }]}><Input onBlur={() => void findExistingClient()} onPressEnter={() => void findExistingClient()} suffix={clientLookupLoading ? <Spin size="small" /> : null} /></Form.Item></Col>
                        <Col xs={24} md={12}><Form.Item name="clientName" label="Nombre / razón social" rules={[{ required: true }]}><Input /></Form.Item></Col>
                        <Col xs={24} md={8}><Form.Item name="clientEmail" label="Correo"><Input type="email" /></Form.Item></Col>
                        <Col xs={24} md={8}><Form.Item name="clientPhone" label="Teléfono"><Input /></Form.Item></Col>
                        <Col xs={24} md={8}><Form.Item name="clientAddress" label="Dirección"><Input /></Form.Item></Col>
                    </Row>
                    <Title level={5}>Detalle y cobro</Title>
                    <Form.List name="details" rules={[{ validator: async (_, value) => { if (!value?.length) throw new Error('Agregue al menos un ítem.'); } }]}>
                        {(fields, { add, remove }, { errors }) => <>
                            {fields.map((field, index) => {
                                const current = watchedDetails[index];
                                const quantity = Number(current?.quantity || 0);
                                const unitPrice = Number(current?.unitPrice || 0);
                                const gross = quantity * unitPrice;
                                const discount = gross * Number(current?.discountPercentage || 0) / 100;
                                const net = gross - discount;
                                const tax = current?.taxTreatment === 'GRAVADO' ? net * Number(current?.taxRate || 0) / 100 : 0;
                                return <Card key={field.key} size="small" style={{ marginBottom: 12 }} title={`Ítem ${index + 1}`} extra={fields.length > 1 ? <Button danger type="text" icon={<DeleteOutlined />} onClick={() => remove(field.name)}>Quitar</Button> : null}>
                                    <Row gutter={12}>
                                        <Col xs={24} md={7}><Form.Item {...field} name={[field.name, 'description']} label="Descripción" rules={[{ required: true, message: 'Ingrese la descripción.' }]}><Input /></Form.Item></Col>
                                        <Col xs={12} md={2}><Form.Item {...field} name={[field.name, 'quantity']} label="Cantidad" rules={[{ required: true, type: 'number', min: 1 }]}><InputNumber min={1} precision={0} style={{ width: '100%' }} /></Form.Item></Col>
                                        <Col xs={12} md={3}><Form.Item {...field} name={[field.name, 'unitPrice']} label="Precio unitario" rules={[{ required: true, type: 'number', min: 0.01 }]}><InputNumber min={0.01} precision={2} style={{ width: '100%' }} /></Form.Item></Col>
                                        <Col xs={12} md={3}><Form.Item {...field} name={[field.name, 'discountPercentage']} label="Descuento %"><InputNumber min={0} max={100} precision={2} style={{ width: '100%' }} /></Form.Item></Col>
                                        <Col xs={12} md={4}><Form.Item {...field} name={[field.name, 'taxRateDefinitionId']} label="Impuesto / IVA" rules={[{ required: true, message: 'Seleccione una tarifa vigente.' }]}><Select onChange={value => {
                                            const selected = effectiveTaxRates.find(item => item.id === value);
                                            form.setFieldValue(['details', field.name, 'taxTreatment'], selected?.taxTreatment || 'NO_OBJETO');
                                            form.setFieldValue(['details', field.name, 'taxRate'], Number(selected?.rate || 0));
                                        }} options={effectiveTaxRates.map(item => ({ value: item.id, label: `${item.name} (${item.rate == null ? 'N/A' : `${Number(item.rate).toFixed(2)} %`})${item.isOrganizationDefault ? ' · Predeterminado' : ''}` }))} /></Form.Item></Col>
                                        <Form.Item {...field} name={[field.name, 'taxTreatment']} hidden><Input /></Form.Item>
                                        <Col xs={12} md={2}><Form.Item {...field} name={[field.name, 'taxRate']} label="IVA %"><InputNumber disabled precision={2} style={{ width: '100%' }} /></Form.Item></Col>
                                        <Col xs={12} md={3}><Form.Item label="Total línea"><Text strong>{new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' }).format(net + tax)}</Text></Form.Item></Col>
                                    </Row>
                                </Card>;
                            })}
                            <Form.ErrorList errors={errors} />
                            <Button type="dashed" block icon={<PlusOutlined />} onClick={() => {
                                const tax = effectiveTaxRates.find(item => item.isOrganizationDefault) || effectiveTaxRates.find(item => item.taxTreatment === 'NO_OBJETO') || effectiveTaxRates[0];
                                add({ description: '', quantity: 1, unitPrice: 0, discountPercentage: 0, taxRateDefinitionId: tax?.id, taxTreatment: tax?.taxTreatment || 'NO_OBJETO', taxRate: Number(tax?.rate || 0) });
                            }}>Agregar ítem</Button>
                        </>}
                    </Form.List>
                    <Row justify="end" style={{ marginTop: 16, marginBottom: 16 }}>
                        <Col><Space direction="vertical" size={2} align="end">
                            <Text>Subtotal: ${invoiceTotals.subtotal.toFixed(2)}</Text>
                            <Text>Descuento: ${invoiceTotals.discount.toFixed(2)}</Text>
                            <Text>IVA: ${invoiceTotals.tax.toFixed(2)}</Text>
                            <Text strong style={{ fontSize: 16 }}>Total: ${invoiceTotals.total.toFixed(2)}</Text>
                        </Space></Col>
                    </Row>
                    <Form.Item name="registerPayment" valuePropName="checked"><Checkbox>Registrar el pago inmediatamente (factura pagada)</Checkbox></Form.Item>
                    {registerPayment && <Row gutter={16}>
                        <Col xs={24} md={10}><Form.Item name="organizationPaymentMethodId" label="Método de pago" rules={[{ required: true }]}><Select options={activePaymentMethods.map(item => ({ value: item.id, label: `${item.name} (${item.code})` }))} /></Form.Item></Col>
                        <Col xs={24} md={8}><Form.Item name="externalTransactionId" label="Referencia de pago" rules={[{ required: paymentRequiresReference, message: 'Este método exige una referencia.' }]}><Input placeholder="Transferencia, comprobante, etc." /></Form.Item></Col>
                        <Col xs={24} md={6}><Text type="secondary">Total a cobrar: ${invoiceTotals.total.toFixed(2)}</Text></Col>
                    </Row>}
                    <Space><Button type="primary" htmlType="submit" icon={<FileAddOutlined />} loading={saving}>Generar factura directa</Button><Text type="secondary">La numeración la reserva el API de forma atómica.</Text></Space>
                </Form>
            )}
            {result?.document && <Alert type="success" showIcon style={{ marginTop: 20 }} message={`Factura ${result.document.documentSeries}-${result.document.documentNumber} creada`} description={result.payment ? 'Pago directo registrado correctamente.' : 'Factura creada como pendiente de pago.'} />}
        </Card>
    );
}

export default DirectSalesDocumentManagement;
